import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, firestore } from "../../config/FirebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { loadStripe } from "@stripe/stripe-js";
import GatheringCustomizationForm from "../../components/GatheringCustomizationForm.js/GatheringCustomizationForm";
import AppPreviewModal from "../../components/AppPreviewModal/AppPreviewModal";
import FetchAnnouncements from "../../api/FetchAnnouncements";
import toCamelCase from "../../logic/toCamelCase";
import "../../styles/views/gathering-dashboard-view.css";
import daysUntil from "../../logic/daysUntil";
import Modal from "../../components/Modal/Modal";
import ModifyGathering from "../../api/ModifyGathering";
import { formatDate } from "../../logic/formatDate";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const functions = getFunctions();

const stripePublishableKey =
  "pk_live_51PtTDNApsFqhrTRLqtI2sjc7kb6l8QNjWKNSm5OkqaTWL1SuUuSbyKMldiaZg6TqkbuAJyjCAuU4AKTRp55ShxIw00uAs0yeuG";

const stripePromise = loadStripe(stripePublishableKey);

const STANDARD_EVENT_ATTENDEE_COUNT = 9999;

const GatheringDashboard = () => {
  const { gatheringID } = useParams();
  const navigate = useNavigate();

  const [gatheringData, setGatheringData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attendeesOverLimit, setAttendeesOverLimit] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [isQuickStartCollapsed, setIsQuickStartCollapsed] = useState(false);

  const handleTestNotification = async () => {
    const sendPushNotification = httpsCallable(
      functions,
      "sendPushNotification",
    );

    try {
      await sendPushNotification({
        gatheringId: gatheringID,
        title: "Test Notification",
        body: "Hello! This is a test notification.",
      });
      alert("Test notification sent!");
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Failed to send notification. Check the console for details.");
    }
  };

  const handlePublish = () => {
    setIsModalOpen(true);
  };

  const handleGenerateQRCode = async () => {
    if (!gatheringData) {
      console.error("Gathering data is not available.");
      return;
    }

    setIsGenerating(true);

    try {
      const dynamicLink = `https://itiniri.app/join/${gatheringData.id}?shortcode=${gatheringData.shortcode}`;

      await ModifyGathering(gatheringData.id, { dynamicLink });

      setGatheringData((prev) => ({
        ...prev,
        dynamicLink,
      }));

      console.log("Dynamic link generated and saved:", dynamicLink);
    } catch (error) {
      console.error("Error generating dynamic link:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (isProcessing) return;

    if (attendeesOverLimit) {
      alert(
        "Please contact us directly for events expecting more than 10,000 attendees.",
      );
      return;
    }

    if (!gatheringID) {
      alert("Unable to start payment. Gathering ID is missing.");
      return;
    }

    const referredCode = gatheringData?.referredCode ?? null;

    setIsProcessing(true);

    try {
      const createPaymentSession = httpsCallable(
        functions,
        "createPaymentSession",
      );

      const result = await createPaymentSession({
        gatheringID,
        attendeeCount: STANDARD_EVENT_ATTENDEE_COUNT,
        redirectUrl: window.location.origin + "/success",
        cancelUrl: window.location.origin + "/cancel",
        referredCode,
      });

      const sessionId = result?.data?.sessionId;

      if (!sessionId) {
        throw new Error("No Stripe session ID was returned.");
      }

      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error("Stripe failed to load.");
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        console.error("Stripe checkout error:", error);
        alert("Unable to redirect to Stripe Checkout. Please try again.");
      }
    } catch (error) {
      console.error("Error creating payment session:", error);
      alert(
        "Unable to create payment session. Please try again. If the issue persists, contact support.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleVisibility = async () => {
    try {
      await ModifyGathering(gatheringID, { visible: !gatheringData.visible });
      setGatheringData((prevData) => ({
        ...prevData,
        visible: !prevData.visible,
      }));
    } catch (error) {
      console.error("Error toggling visibility:", error);
    }
  };

  const fetchGatheringAndAnnouncements = useCallback(async () => {
    if (!gatheringID || !auth.currentUser?.uid) return;

    try {
      const docRef = doc(firestore, "Gatherings", gatheringID);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        navigate("/");
        return;
      }

      const data = toCamelCase(docSnap.data());

      const userDocRef = doc(firestore, "Users", auth.currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      const isAdmin = userDocSnap.exists() && userDocSnap.data().admin;

      let isTeamMember = false;

      if (data.teamEmails) {
        const teamEmails = Array.isArray(data.teamEmails)
          ? data.teamEmails
          : Object.values(data.teamEmails);

        isTeamMember = teamEmails.includes(auth.currentUser.email);
      }

      if (auth.currentUser?.uid === data.hostID || isAdmin || isTeamMember) {
        setGatheringData(data);

        const fetchedAnnouncements = await FetchAnnouncements(gatheringID);
        setAnnouncements(fetchedAnnouncements.map((ann) => toCamelCase(ann)));
        setIsAuthorized(true);
      } else {
        navigate("/");
        return;
      }
    } catch (error) {
      console.error("Error fetching gathering dashboard data:", error);
      navigate("/");
      return;
    } finally {
      setLoading(false);
    }
  }, [gatheringID, navigate]);

  const handleCustomizationSaved = useCallback(async () => {
    await fetchGatheringAndAnnouncements();
    setPreviewRefreshKey((prev) => prev + 1);
  }, [fetchGatheringAndAnnouncements]);

  useEffect(() => {
    document.body.style.overflow = "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    fetchGatheringAndAnnouncements();
  }, [fetchGatheringAndAnnouncements]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthorized) {
    return <div>Access Denied</div>;
  }

  return (
    <div className="dashboard-container">
      <button className="return-home-button" onClick={() => navigate("/")}>
        Go back
      </button>


      <div className="event-header-card">
        <div className="event-info-section">
          <h1 className="event-title">{gatheringData?.gatheringName}</h1>

          <div className="event-meta">
            <div className="meta-item">
              <span className="meta-icon">📍</span>
              <span className="meta-text">{gatheringData.location}</span>
            </div>

            <div className="meta-item">
              <span className="meta-icon">📅</span>
              <span className="meta-text">
                {formatDate(gatheringData.startDate)} -{" "}
                {formatDate(gatheringData.endDate)}
              </span>
            </div>
          </div>

          <div className="event-actions">
            {!gatheringData.published && (
              <>
                <button className="publish-button" onClick={handlePublish}>
                  Publish Event
                </button>

                <button
                  className="visibility-toggle"
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  Preview App
                </button>
              </>
            )}

            {gatheringData.published && (
              <div className="published-controls">
                <span className="published-badge">Published</span>

                <button
                  className="visibility-toggle"
                  onClick={toggleVisibility}
                >
                  {gatheringData?.visible ? "Hide Event" : "Make Visible"}
                </button>

                <button
                  className="visibility-toggle"
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  Preview App
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="countdown-section">
          <div className="countdown-container">
            <div className="countdown-number">
              {daysUntil(gatheringData.startDate)}
            </div>

            {daysUntil(gatheringData.startDate) >= 1 && (
              <div className="countdown-label">
                {daysUntil(gatheringData.startDate) === 1 ? "Day" : "Days"}
                <br />
                Until Event
              </div>
            )}
          </div>

          <div className="countdown-decoration">
            <div className="countdown-ring"></div>
          </div>
        </div>
      </div>

      <div className="content-area">
        <div className="left-content">
          <div id="customization-section" className="rounded-div form-div">
            <h4>Customize Your Event</h4>
            <p>
              IMPORTANT: When making changes, make sure you click the "Save
              Changes" button in order to save your updates.
            </p>
            <GatheringCustomizationForm
              onSaveSuccess={handleCustomizationSaved}
            />
          </div>
        </div>

        <div className="mobile-message">
          <p>
            We're sorry, but the event customization form is not yet available
            on mobile devices. Please access this page from a desktop computer.
          </p>
        </div>
      </div>

      <AppPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        gatheringID={gatheringID}
        refreshKey={previewRefreshKey}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2>Publish Event</h2>

        <p>
          Events expecting fewer than 10,000 attendees are charged a flat
          publishing fee of <strong>$100.00</strong>.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            margin: "16px 0",
            lineHeight: 1.4,
          }}
        >
          <input
            type="checkbox"
            checked={attendeesOverLimit}
            onChange={(e) => setAttendeesOverLimit(e.target.checked)}
            style={{ marginTop: "4px" }}
          />
          <span>Is your event expecting more than 10,000 attendees?</span>
        </label>

        {attendeesOverLimit ? (
          <div>
            <p>
              For events expecting more than 10,000 attendees, please contact us
              directly for pricing and setup support.
            </p>

            <p>
              Email: <strong>YOUR_CONTACT_EMAIL_HERE</strong>
            </p>

            <p>
              Phone: <strong>YOUR_CONTACT_PHONE_HERE</strong>
            </p>

            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              disabled={isProcessing}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p>
              Total Price: <strong>$100.00</strong>
            </p>

            <button onClick={handleProceedToPayment} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Proceed to Payment"}
            </button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default GatheringDashboard;
